import type { Ref } from 'vue';
import type { RouteRecordName } from 'vue-router';
import { computed } from 'vue';

import type {
  IFinanceMonthAdditionalTableData,
  IFinanceMonthListItem,
} from '@modules/finance/submodules/month-report/types';
import type { FinanceMonthAasmState } from '@modules/finance/types';
import type { ITableMultiselectActionParams } from '@modules/tables/components/VTable/types';
import type {
  IExtendedTableDataResponse,
  ITableMultiselectActions,
  ITableState,
} from '@modules/tables/types';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { EAasmState } from '@modules/core/enums';
import {
  ADMIN_FINANCE_LIST_ROUTE_NAME,
  AFFILIATE_FINANCE_LIST_ROUTE_NAME,
  FINANCE_PERMISSIONS,
  TRANSLATION_GROUP_FINANCE,
} from '@modules/finance/constants';
import {
  useApproveFinanceMonthMutation,
  useCancelTransferPayoutFinanceMonthMutation,
  usePayFinanceMonthMutation,
  useProcessFinanceMonthMutation,
  useTransferPayoffFinanceMonthMutation,
  useTransferPayoutFinanceMonthMutation,
} from '@modules/finance/submodules/month-report/mutations';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import { watchImmediate } from '@vueuse/core';

export interface FinanceMonthListPageComposable {
  backRouteName: Ref<RouteRecordName>;
  isInProcess: Ref<boolean>;
  isCloseMonthAvailable: Ref<boolean>;
  isApproveMonthLoading: Ref<boolean>;
  onApproveFinanceMonth(event: MouseEvent): void;
  isApproved: Ref<boolean>;
  isAdminNamespace: Ref<boolean>;
  isPayMonthLoading: Ref<boolean>;
  onPayFinanceMonth(event: MouseEvent): void;
  multiselectOptions: Ref<ITableMultiselectActions>;
  onMultiselectAction(params: ITableMultiselectActionParams): void;
}

export default function useFinanceMonthListPage(
  data: Ref<IExtendedTableDataResponse<IFinanceMonthListItem, IFinanceMonthAdditionalTableData> | undefined>,
  tableState: Ref<ITableState>,
  date: Ref<string>,
  setData: (updater: (data: IExtendedTableDataResponse<IFinanceMonthListItem, IFinanceMonthAdditionalTableData> | undefined) => IExtendedTableDataResponse<IFinanceMonthListItem, IFinanceMonthAdditionalTableData> | undefined) => void,
): FinanceMonthListPageComposable {
  const { confirmDelete } = useConfirmDialogs();
  const { getTranslation } = useI18n();
  const { hasPermission } = usePermissionsStore();
  const { isAdminNamespace } = useCurrentNamespace();

  const backRouteName = computed(() => (isAdminNamespace.value ? ADMIN_FINANCE_LIST_ROUTE_NAME : AFFILIATE_FINANCE_LIST_ROUTE_NAME));
  const isInProcess = computed(() => data.value?.month_payment_data?.state === EAasmState.IN_PROCESS);
  const isCloseMonthAvailable = computed(() => !!data.value?.month_payment_data?.close_month_available);
  const isApproved = computed(() => data.value?.month_payment_data?.state === EAasmState.APPROVED);
  const hasChangeStatusPermission = hasPermission(FINANCE_PERMISSIONS.CHANGE_STATUS);
  const canBeProcessed = computed(() => data.value?.month_payment_data?.state === EAasmState.CAN_BE_PROCESS && hasChangeStatusPermission.value);

  const { mutateAsync: approveMonth, isLoading: isApproveMonthLoading } = useApproveFinanceMonthMutation();
  const { mutateAsync: payMonth, isLoading: isPayMonthLoading } = usePayFinanceMonthMutation();
  const { mutateAsync: processMonth } = useProcessFinanceMonthMutation();
  const { mutateAsync: transferPayout } = useTransferPayoutFinanceMonthMutation();
  const { mutateAsync: cancelTransferPayout } = useCancelTransferPayoutFinanceMonthMutation();
  const { mutateAsync: transferPayoff } = useTransferPayoffFinanceMonthMutation();

  function updateStatus(state: FinanceMonthAasmState): void {
    setData((queryData) => {
      if (!queryData?.month_payment_data) {
        return queryData;
      }

      return {
        ...queryData,
        month_payment_data: {
          ...queryData.month_payment_data,
          state,
          close_month_available: state === EAasmState.IN_PROCESS ? false : queryData.month_payment_data.close_month_available,
        },
      };
    });
  }

  function onApproveFinanceMonth(event: MouseEvent): void {
    confirmDelete({
      event,
      header: getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_CHANGE_QUESTION').value,
      message: getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_GOAL_APPROVED').value,
      acceptLabel: getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_YES').value,
      rejectLabel: getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_NO').value,
      accept: () => {
        void approveMonth({
          date_of_payment: date.value,
        }).then(() => {
          updateStatus(EAasmState.APPROVED);
        });
      },
    });
  }

  function onPayFinanceMonth(event: MouseEvent): void {
    confirmDelete({
      event,
      header: getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_CHANGE_QUESTION').value,
      message: getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_GOAL_PAID').value,
      acceptLabel: getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_YES').value,
      rejectLabel: getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_NO').value,
      accept: () => {
        void payMonth({
          date_of_payment: date.value,
        }).then(() => {
          updateStatus(EAasmState.PAID);
        });
      },
    });
  }

  const multiselectOptions = computed<ITableMultiselectActions>(() => {
    const actions: ITableMultiselectActions = {};

    if (hasPermission(FINANCE_PERMISSIONS.TRANSFER_PAYMENT).value) {
      actions.transferPayout = {
        label: getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_TRANSFER_PAYOUT').value,
      };

      actions.cancelTransferPayout = {
        label: getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_CANCEL_TRANSFER_PAYOUT').value,
      };
    }

    if (hasPermission(FINANCE_PERMISSIONS.TRANSFER_PAYOFF).value) {
      actions.transferPayoff = {
        label: getTranslation(TRANSLATION_GROUP_FINANCE, 'MONTH_REPORT_TRANSFER_PAYOFF').value,
      };
    }

    return actions;
  });

  function onMultiselectAction(params: ITableMultiselectActionParams): void {
    switch (params.actionId) {
      case 'transferPayout':
        void transferPayout({
          ids: params.selection,
          date_of_payment: date.value,
        });
        break;
      case 'cancelTransferPayout':
        void cancelTransferPayout({
          ids: params.selection,
          date_of_payment: date.value,
        });
        break;
      case 'transferPayoff':
        void transferPayoff({
          ids: params.selection,
          date_of_payment: date.value,
          page: tableState.value.page || 1,
        });
        break;
      default:
        break;
    }
  }

  watchImmediate(canBeProcessed, (newValue) => {
    if (newValue) {
      void processMonth({
        date_of_payment: date.value,
      }).then(() => {
        updateStatus(EAasmState.IN_PROCESS);
      });
    }
  });

  return {
    isAdminNamespace,
    backRouteName,
    isInProcess,
    isCloseMonthAvailable,
    isApproveMonthLoading,
    onApproveFinanceMonth,
    isApproved,
    isPayMonthLoading,
    onPayFinanceMonth,
    multiselectOptions,
    onMultiselectAction,
  };
}
